import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Maximize2 } from 'lucide-react';
import { siteMeta } from '../data';
import { imageUrl } from '../lib/images';

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('Alle');
  const [heroImages, setHeroImages] = useState([
    "/_assets/th-skovservice-galleri-projekter.jpg"
  ]);
  const [heroImageIndex, setHeroImageIndex] = useState(0);

  useEffect(() => {
    if (items.length > 0) {
      const allImages = items.flatMap(item => item.image || []);
      if (allImages.length > 0) {
        const shuffled = [...allImages].sort(() => Math.random() - 0.5);
        setHeroImages(shuffled);
      }
    }
  }, [items]);

  useEffect(() => {
    if (heroImages.length <= 1) return;
    const timer = setInterval(() => {
      setHeroImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [heroImages]);

  useEffect(() => {
    fetch('/_api/public/content/gallery')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch');
        return res.json();
      })
      .then(data => setItems(data))
      .catch(err => console.error('Error fetching gallery:', err));
  }, []);

  const categories = ['Alle', ...new Set(items.map(item => item.category?.da).filter(Boolean))];
  const filteredItems = filter === 'Alle' ? items : items.filter(item => item.category?.da === filter);

  return (
    <>
      <Helmet>
        <title>Galleri | {siteMeta.title}</title>
        <meta name="description" content="Se billeder af vores arbejde med træfældning, topkapning og skovservice." />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-stone-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <AnimatePresence mode="wait">
            {heroImages.length > 0 && (
              <motion.img
                key={heroImageIndex}
                src={imageUrl(heroImages[heroImageIndex], 'width=1920,quality=85,format=auto')}
                alt="TH Skovservice - Vores arbejde og projekter inden for skovservice på Mors og i Thy"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
                className="w-full h-full object-cover"
              />
            )}
          </AnimatePresence>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center pt-12">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[2rem] md:text-7xl font-bold mb-6 tracking-tight"
          >
            Vores Arbejde
          </motion.h1>
          <p className="text-base md:text-xl text-stone-300 max-w-2xl mx-auto leading-relaxed">
            Et udpluk af de mange opgaver vi har løst for vores kunder. Kvalitet og sikkerhed i højsædet.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="pt-8 pb-24 bg-white">
        <div className="container mx-auto px-4">
          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-6 py-2 rounded-none text-sm font-bold transition-all ${
                  filter === cat 
                    ? 'bg-emerald-900 text-white' 
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <motion.div 
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            <AnimatePresence mode='popLayout'>
              {filteredItems.map((item) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4 }}
                  className="group relative cursor-pointer overflow-hidden rounded-none aspect-[4/3]"
                  onClick={() => setSelectedImage(item)}
                >
                  <img 
                    src={imageUrl(item.image?.[0], 'width=800,quality=85,format=auto')} 
                    alt={`${item.title?.da || 'Skovservice'} - TH Skovservice projekt på Mors og i Thy`} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-stone-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-8">
                    <p className="text-emerald-400 text-xs font-bold uppercase tracking-widest mb-2">{item.category?.da}</p>
                    <h3 className="text-white text-xl font-bold">{item.title?.da}</h3>
                    <div className="absolute top-8 right-8 w-10 h-10 bg-white/20 backdrop-blur-md rounded-none flex items-center justify-center text-white">
                      <Maximize2 className="w-5 h-5" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-stone-900/95 flex items-center justify-center p-4 md:p-12"
            onClick={() => setSelectedImage(null)}
          >
            <button 
              className="absolute top-8 right-8 text-white hover:text-emerald-400 transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <X className="w-10 h-10" />
            </button>
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-6xl w-full max-h-full overflow-hidden rounded-none"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={imageUrl(selectedImage.image?.[0], 'width=1600,quality=90,format=auto')} 
                alt={`${selectedImage.title?.da || 'Skovservice'} - TH Skovservice projekt på Mors og i Thy`} 
                className="w-full h-auto object-contain"
              />
              <div className="bg-stone-900/80 backdrop-blur-md p-6 text-white text-center">
                <h3 className="text-2xl font-bold">{selectedImage.title?.da}</h3>
                <p className="text-emerald-400">{selectedImage.category?.da}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
